// Role-first onboarding (mobile).
//
// Philosophy: don't teach the app — understand the user. The flow is
//   Welcome → "How will you use ProRentz?" → 3 outcome-led pages for THAT
//   role → ready → login (already personalised, role pre-selected).
//
// Everything here is presentation + local state. It writes exactly two
// things: the chosen role (handed to App via onDone) and a "seen" flag in
// localStorage. No API calls, no account creation.
//
// Copy goes through `t.*` with English fallbacks so translations can land
// later without touching this file.
import React, { useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";

const ONBOARD_KEY = "prorentz_onboarded_v1";

export const isOnboarded = () => {
    try { return window.localStorage.getItem(ONBOARD_KEY) === "1"; } catch (_) { return false; }
};
export const markOnboarded = () => {
    try { window.localStorage.setItem(ONBOARD_KEY, "1"); } catch (_) { /* private mode */ }
};

// Soft haptic on selection — Android/Chrome only, silently ignored elsewhere.
const tap = () => { try { navigator.vibrate && navigator.vibrate(8); } catch (_) {} };

/* ── Illustrations ──────────────────────────────────────────────────────
   Inline SVG (no assets, no network). Each sits on a soft tinted disc so
   the pages feel consistent. `a` = accent (coral), `b` = indigo. */
const Illo = ({ name, a, b, dk }) => {
    const disc = dk ? "rgba(255,255,255,0.05)" : "rgba(17,24,39,0.035)";
    const line = dk ? "rgba(255,255,255,0.16)" : "rgba(17,24,39,0.10)";
    const G = {
        building: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <rect x="46" y="52" width="48" height="86" rx="7" stroke={b} />
                <rect x="94" y="74" width="42" height="64" rx="7" stroke={a} />
                {[64, 84, 104].map((y) => [56, 74].map((x) => <rect key={`${x}${y}`} x={x} y={y} width="12" height="12" rx="3" stroke={b} opacity="0.55" />))}
                {[90, 110].map((y) => [104, 120].map((x) => <rect key={`s${x}${y}`} x={x} y={y} width="10" height="10" rx="3" stroke={a} opacity="0.55" />))}
                <path d="M34 138h114" stroke={line} />
            </g>
        ),
        payments: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <rect x="40" y="58" width="102" height="62" rx="12" stroke={b} />
                <path d="M40 80h102" stroke={b} opacity="0.5" />
                <rect x="54" y="94" width="26" height="8" rx="4" stroke={a} />
                <path d="M60 140v-14M84 140v-26M108 140v-20M132 140v-34" stroke={a} />
            </g>
        ),
        organised: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <path d="M42 66a6 6 0 016-6h24l10 12h34a6 6 0 016 6v52a6 6 0 01-6 6H48a6 6 0 01-6-6z" stroke={b} />
                <path d="M62 96h44M62 112h30" stroke={b} opacity="0.5" />
                <circle cx="126" cy="112" r="20" stroke={a} />
                <path d="M118 112l6 6 12-13" stroke={a} />
            </g>
        ),
        home: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <path d="M46 92l44-36 44 36v46a6 6 0 01-6 6H52a6 6 0 01-6-6z" stroke={b} />
                <path d="M78 144v-30h24v30" stroke={a} />
                <path d="M34 96l56-46 56 46" stroke={a} opacity="0.45" />
            </g>
        ),
        wallet: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <rect x="42" y="64" width="100" height="66" rx="12" stroke={b} />
                <path d="M42 86h100" stroke={b} opacity="0.45" />
                <circle cx="118" cy="108" r="9" stroke={a} />
                <path d="M64 108h22" stroke={a} />
            </g>
        ),
        chat: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <path d="M44 68a8 8 0 018-8h50a8 8 0 018 8v28a8 8 0 01-8 8H70l-16 14v-14h-2a8 8 0 01-8-8z" stroke={b} />
                <path d="M118 88h18a8 8 0 018 8v24a8 8 0 01-8 8h-2v12l-14-12h-18a8 8 0 01-8-8" stroke={a} />
                <path d="M62 78h30M62 90h18" stroke={b} opacity="0.5" />
            </g>
        ),
        jobs: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <path d="M62 76V66a14 14 0 0128 0v10" stroke={a} />
                <rect x="40" y="76" width="104" height="58" rx="12" stroke={b} />
                <path d="M40 100h104" stroke={b} opacity="0.45" />
                <circle cx="122" cy="70" r="12" fill={a} stroke="none" />
            </g>
        ),
        checklist: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <rect x="46" y="52" width="92" height="88" rx="12" stroke={b} />
                <path d="M62 78l7 7 13-14M62 106l7 7 13-14" stroke={a} />
                <path d="M96 80h28M96 108h20" stroke={b} opacity="0.5" />
            </g>
        ),
        earnings: (
            <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4">
                <rect x="42" y="70" width="100" height="60" rx="12" stroke={b} />
                <circle cx="92" cy="100" r="17" stroke={a} />
                <path d="M86 93h12M86 100h12M96 93c0 9-10 4-10 14l12-6" stroke={a} />
            </g>
        ),
    };
    return (
        <svg viewBox="0 0 180 180" width="176" height="176" aria-hidden="true">
            <circle cx="90" cy="96" r="76" fill={disc} />
            {G[name] || G.building}
        </svg>
    );
};

export default function Onboarding({ onDone }) {
    const { t, h } = useContext(Ctx);
    const [step, setStep] = useState(0);   // 0 welcome · 1 role · 2-4 pages · 5 ready
    const [role, setRole] = useState(null);

    const ROLES = [
        {
            key: "landlord", emoji: "🏠",
            title: t.landlord || "Landlord",
            lines: [t.obLlA || "Manage properties", t.obLlB || "Collect rent", t.obLlC || "Track maintenance"],
            pages: [
                { illo: "building", title: t.obLl1 || "Manage every property in one place.", sub: t.obLl1s || "Track tenants, rent, agreements and maintenance effortlessly." },
                { illo: "payments", title: t.obLl2 || "Never miss another payment.", sub: t.obLl2s || "Automatic reminders, payment tracking and a clear income overview." },
                { illo: "organised", title: t.obLl3 || "Everything stays organised.", sub: t.obLl3s || "Handle maintenance, documents and communication from one app." },
            ],
            ready: t.obLlReady || "Let's manage your property.",
        },
        {
            key: "tenant", emoji: "🏡",
            title: t.tenant || "Tenant",
            lines: [t.obTnA || "Pay rent", t.obTnB || "Raise requests", t.obTnC || "Store agreements"],
            pages: [
                { illo: "home", title: t.obTn1 || "Your rental, simplified.", sub: t.obTn1s || "Everything about your home in one place." },
                { illo: "wallet", title: t.obTn2 || "Pay rent in seconds.", sub: t.obTn2s || "UPI, cards and bank transfer — with automatic reminders." },
                { illo: "chat", title: t.obTn3 || "Need help?", sub: t.obTn3s || "Raise maintenance requests and chat with your landlord." },
            ],
            ready: t.obTnReady || "Let's settle into your home.",
        },
        {
            key: "service", emoji: "🛠",
            title: t.servicePerson || "Service Professional",
            lines: [t.obSpA || "Receive jobs", t.obSpB || "Update progress", t.obSpC || "Get paid"],
            pages: [
                { illo: "jobs", title: t.obSp1 || "Receive jobs instantly.", sub: t.obSp1s || "New work reaches you the moment it's assigned." },
                { illo: "checklist", title: t.obSp2 || "Update work progress.", sub: t.obSp2s || "Keep everyone in the loop as the job moves along." },
                { illo: "earnings", title: t.obSp3 || "Track completed work and payments.", sub: t.obSp3s || "See what you've finished and what you've earned." },
            ],
            ready: t.obSpReady || "Let's get you to work.",
        },
    ];

    const sel = ROLES.find(r => r.key === role);
    const finish = () => { markOnboarded(); onDone(role); };
    const skip = () => { markOnboarded(); onDone(role); };

    // Shared chrome ───────────────────────────────────────────────────────
    const cta = {
        width: "100%", height: 56, borderRadius: 18, border: "none",
        background: h.ac, color: "#fff", fontSize: 16, fontWeight: 700,
        fontFamily: "inherit", cursor: "pointer", letterSpacing: "-0.2px",
        boxShadow: `0 10px 24px ${h.ac}40`,
        transition: "transform .2s cubic-bezier(.32,.72,.22,1)",
    };
    const press = {
        onPointerDown: (e) => { e.currentTarget.style.transform = "scale(0.97)"; },
        onPointerUp: (e) => { e.currentTarget.style.transform = "scale(1)"; },
        onPointerLeave: (e) => { e.currentTarget.style.transform = "scale(1)"; },
    };
    const Dots = ({ n, i }) => (
        <div style={{ display: "flex", gap: 7, justifyContent: "center", marginBottom: 22 }}>
            {Array.from({ length: n }).map((_, k) => (
                <span key={k} style={{
                    width: k === i ? 7 : 6, height: k === i ? 7 : 6, borderRadius: "50%",
                    background: k === i ? h.ac : (h.dk ? "rgba(255,255,255,0.22)" : "rgba(17,24,39,0.16)"),
                    transition: "all .24s cubic-bezier(.32,.72,.22,1)",
                }} />
            ))}
        </div>
    );
    const Skip = ({ onClick }) => (
        <button onClick={onClick} style={{
            position: "absolute", top: "max(14px, env(safe-area-inset-top))", right: 16,
            background: "none", border: "none", color: h.tt || h.ts,
            fontSize: 13.5, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", padding: 8, zIndex: 2,
        }}>{t.skip || "Skip"}</button>
    );

    const shell = {
        position: "fixed", inset: 0, background: h.bg, zIndex: 4000,
        display: "flex", flexDirection: "column",
        padding: "0 24px calc(28px + env(safe-area-inset-bottom))",
        overflowY: "auto",
    };

    const anim = { animation: "obIn .28s cubic-bezier(.32,.72,.22,1) both" };

    const keyframes = (
        <style>{`
      @keyframes obIn { from { opacity: 0; transform: translateY(10px) scale(0.99); } to { opacity: 1; transform: none; } }
      @keyframes obPop { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
      @media (prefers-reduced-motion: reduce) { .ob-anim, .ob-pop { animation: none !important; } }
    `}</style>
    );

    /* ── 0 · Welcome ─────────────────────────────────────────────────── */
    if (step === 0) return (
        <div style={shell}>
            {keyframes}
            <div className="ob-anim" style={{ ...anim, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", paddingTop: 40 }}>
                <div style={{
                    width: 84, height: 84, borderRadius: 26, background: h.gl,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: `0 16px 34px ${h.ac}45`, marginBottom: 30,
                }}>
                    <span style={{ fontSize: 40, lineHeight: 1 }}>🏠</span>
                </div>
                <div style={{ color: h.ts, fontSize: 15, fontWeight: 500 }}>{t.welcomeTo || "Welcome to"}</div>
                <h1 style={{ color: h.tx, fontSize: 40, fontWeight: 800, letterSpacing: "-1.4px", margin: "4px 0 14px" }}>
                    Pro<span style={{ color: h.ac }}>Rentz</span>
                </h1>
                <p style={{ color: h.ts, fontSize: 16, lineHeight: 1.5, margin: 0, maxWidth: 280 }}>
                    {t.obTagline || "Property management, made beautifully."}
                </p>
            </div>
            <button style={cta} {...press} onClick={() => setStep(1)}>{t.getStarted || "Get Started"}</button>
        </div>
    );

    /* ── 1 · Who are you? ────────────────────────────────────────────── */
    if (step === 1) return (
        <div style={shell}>
            {keyframes}
            <Skip onClick={skip} />
            <div className="ob-anim" style={{ ...anim, flex: 1, display: "flex", flexDirection: "column", paddingTop: "max(74px, calc(env(safe-area-inset-top) + 62px))" }}>
                <h2 style={{ color: h.tx, fontSize: 27, fontWeight: 700, letterSpacing: "-0.8px", margin: "0 0 6px", lineHeight: 1.2 }}>
                    {t.obHowUse || "How will you use ProRentz?"}
                </h2>
                <p style={{ color: h.ts, fontSize: 14, margin: "0 0 22px" }}>{t.obPickOne || "Pick one — we'll set the app up for you."}</p>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {ROLES.map((r) => {
                        const on = role === r.key;
                        return (
                            <button key={r.key} onClick={() => { tap(); setRole(r.key); }}
                                style={{
                                    textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                                    background: h.cd, borderRadius: 22, padding: "16px 18px",
                                    border: `1.5px solid ${on ? h.ac : "transparent"}`,
                                    boxShadow: on ? `0 12px 26px ${h.ac}2e` : h.sh,
                                    transform: on ? "scale(1.015)" : "scale(1)",
                                    transition: "all .24s cubic-bezier(.32,.72,.22,1)",
                                    display: "flex", gap: 14, alignItems: "flex-start",
                                    WebkitTapHighlightColor: "transparent",
                                }}>
                                <span style={{
                                    width: 46, height: 46, borderRadius: 15, flexShrink: 0,
                                    background: on ? `${h.ac}14` : (h.dk ? "rgba(255,255,255,0.05)" : "rgba(17,24,39,0.04)"),
                                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
                                    transition: "background .24s",
                                }}>{r.emoji}</span>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: "block", color: h.tx, fontSize: 16.5, fontWeight: 700, letterSpacing: "-0.3px" }}>{r.title}</span>
                                    <span style={{ display: "block", color: h.ts, fontSize: 12.5, lineHeight: 1.55, marginTop: 3 }}>
                                        {r.lines.join(" · ")}
                                    </span>
                                </span>
                                <span style={{
                                    width: 22, height: 22, borderRadius: "50%", flexShrink: 0, marginTop: 12,
                                    border: `2px solid ${on ? h.ac : (h.dk ? "rgba(255,255,255,0.18)" : "rgba(17,24,39,0.14)")}`,
                                    background: on ? h.ac : "transparent",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    transition: "all .2s",
                                }}>
                                    {on && <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,6 5,9 10,3" /></svg>}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <button
                style={{ ...cta, opacity: role ? 1 : 0.42, boxShadow: role ? cta.boxShadow : "none", cursor: role ? "pointer" : "default" }}
                {...(role ? press : {})}
                disabled={!role}
                onClick={() => role && setStep(2)}
            >{t.continue || "Continue"}</button>
        </div>
    );

    /* ── 2-4 · Role-specific pages ───────────────────────────────────── */
    if (step >= 2 && step <= 4 && sel) {
        const i = step - 2;
        const pg = sel.pages[i];
        return (
            <div style={shell}>
                {keyframes}
                <Skip onClick={skip} />
                <div key={step} className="ob-anim" style={{ ...anim, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", paddingTop: 56 }}>
                    <div className="ob-pop" style={{ animation: "obPop .32s cubic-bezier(.32,.72,.22,1) both", marginBottom: 30 }}>
                        <Illo name={pg.illo} a={h.ac} b={h.in || h.ac} dk={h.dk} />
                    </div>
                    <h2 style={{ color: h.tx, fontSize: 26, fontWeight: 700, letterSpacing: "-0.8px", lineHeight: 1.22, margin: "0 0 12px", maxWidth: 320 }}>
                        {pg.title}
                    </h2>
                    <p style={{ color: h.ts, fontSize: 15, lineHeight: 1.55, margin: 0, maxWidth: 300 }}>{pg.sub}</p>
                </div>
                <Dots n={3} i={i} />
                <button style={cta} {...press} onClick={() => setStep(step + 1)}>{t.continue || "Continue"}</button>
            </div>
        );
    }

    /* ── 5 · Ready ───────────────────────────────────────────────────── */
    return (
        <div style={shell}>
            {keyframes}
            <div className="ob-anim" style={{ ...anim, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", paddingTop: 40 }}>
                <div className="ob-pop" style={{
                    animation: "obPop .34s cubic-bezier(.32,.72,.22,1) both",
                    width: 92, height: 92, borderRadius: "50%", background: `${h.gn}14`,
                    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28,
                }}>
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke={h.gn} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="4,12.5 9.5,18 20,6.5" />
                    </svg>
                </div>
                <h2 style={{ color: h.tx, fontSize: 27, fontWeight: 700, letterSpacing: "-0.8px", margin: "0 0 10px" }}>
                    {t.obReady || "Everything is ready."}
                </h2>
                <p style={{ color: h.ts, fontSize: 15.5, lineHeight: 1.5, margin: 0, maxWidth: 290 }}>
                    {sel ? sel.ready : (t.obReadySub || "Let's get you started.")}
                </p>
            </div>
            <button style={cta} {...press} onClick={finish}>{t.continue || "Continue"}</button>
        </div>
    );
}
